import { sdk } from '@/lib/sdk'
import type { DownloadConfirmResult, DownloadUpload } from '@/types/downloads'
import { mimeOf } from './downloadUtils'
import { announcePlanLimit, isStorageLimitError } from '@/lib/planLimitNotice'

export class UploadCanceled extends Error {}
export class StorageRefused extends Error {}

export interface UploadProgress {
  percent: number
  /** Smoothed send rate; absent until there is a second of samples. */
  bytesPerSecond?: number
  secondsLeft?: number
}

/** Rate samples are taken at most this often, and smoothed, so the estimate does not jump around. */
const SAMPLE_MS = 1000
const SMOOTHING = 0.3

/**
 * PUT straight to storage with the exact headers and size the API signed; reports progress, rate
 * and time left. The `File` goes to `xhr.send` as is, so the browser streams it from disk and
 * never holds a 4 GB copy in memory. The URL is never logged.
 */
const putToStorage = (upload: DownloadUpload, file: File, onProgress: (progress: UploadProgress) => void, signal: AbortSignal) =>
  new Promise<void>((resolve, reject) => {
    const xhr = new XMLHttpRequest()
    xhr.open(upload.method || 'PUT', upload.uploadUrl)
    for (const [name, value] of Object.entries(upload.headers || {})) xhr.setRequestHeader(name, value)
    let lastAt = performance.now()
    let lastLoaded = 0
    let rate: number | undefined
    xhr.upload.onprogress = (event) => {
      if (!event.lengthComputable) return
      const now = performance.now()
      if (now - lastAt >= SAMPLE_MS) {
        const sample = ((event.loaded - lastLoaded) * 1000) / (now - lastAt)
        rate = rate === undefined ? sample : rate * (1 - SMOOTHING) + sample * SMOOTHING
        lastAt = now
        lastLoaded = event.loaded
      }
      onProgress({
        percent: Math.min(100, Math.floor((event.loaded / event.total) * 100)),
        bytesPerSecond: rate,
        secondsLeft: rate && rate > 0 ? Math.ceil((event.total - event.loaded) / rate) : undefined
      })
    }
    xhr.onload = () => (xhr.status >= 200 && xhr.status < 300 ? resolve() : reject(new StorageRefused()))
    /* A dropped connection or a stalled network: the screen offers "Try again", which asks for a new link. */
    xhr.onerror = () => reject(new StorageRefused())
    xhr.ontimeout = () => reject(new StorageRefused())
    xhr.onabort = () => reject(new UploadCanceled())
    signal.addEventListener('abort', () => xhr.abort())
    xhr.send(file)
  })

/**
 * upload-url, then the direct PUT, then confirm (or replace, when `replaceFileId` is given).
 * Nothing changes on the item until the confirm succeeds.
 */
export const uploadItemFile = async (
  itemId: string,
  file: File,
  onProgress: (progress: UploadProgress) => void,
  signal: AbortSignal,
  replaceFileId?: string
): Promise<DownloadConfirmResult> => {
  const upload = await sdk.downloads.requestUpload(itemId, { name: file.name, mimeType: mimeOf(file), size: file.size }).catch((error: unknown) => {
    /* F-34 `media.storageGb`: the form shows the refusal; the toast offers the plan change. */
    if (isStorageLimitError(error)) announcePlanLimit({ kind: 'storage' })
    throw error
  })
  if (signal.aborted) throw new UploadCanceled()
  await putToStorage(upload, file, onProgress, signal)
  const body = { key: upload.key, name: upload.name || file.name, size: file.size }
  return replaceFileId
    ? sdk.downloads.replaceFile(itemId, replaceFileId, body)
    : sdk.downloads.confirmFile(itemId, body)
}
